import type { WidgetDefinition } from '@shared/contracts/customisation';
import type { CapabilityId } from '@/services/entitlements';

/**
 * Mobile home widget registry.
 *
 * Conforms to `WidgetDefinition` in the package's customisation contracts and
 * adds the mobile-specific governance the addendum requires:
 *
 *  - `removable: false` marks a widget that personalisation may reorder within
 *    its constraints but may never delete. Mandatory security, payment-review,
 *    risk, SCA, approval and legal elements are locked (11_BINDING_USER_ADDENDUM).
 *  - `pinnedIndex` encodes an ordering constraint the requirements impose
 *    directly, so no layout — manual or AI-proposed — can violate it.
 *
 * TRACE:TT-R109-01 — the mobile home offers a curated, reduced widget set.
 * TRACE:UADD-V5-SMEM-PERS-001-02 — the catalogue is what a user may add from.
 */

export const MOBILE_WIDGET_IDS = [
  'total-balance',
  'quick-actions',
  'needs-attention',
  'money-flow',
  'recent-activity',
  'cards-summary',
  'finance-shortcuts',
  'accounts-list',
] as const;

export type MobileWidgetId = (typeof MOBILE_WIDGET_IDS)[number];

export type MobileWidgetDefinition = WidgetDefinition & {
  readonly id: MobileWidgetId;
  /** Short line shown in the add-widget catalogue. */
  readonly description: string;
  /** Locked widgets cannot be removed by a user or by the AI composer. */
  readonly removable: boolean;
  /** Hard ordering constraint, when a requirement fixes the position. */
  readonly pinnedIndex?: number;
  /** Capability that must resolve before the widget renders or is offered. */
  readonly capability?: CapabilityId;
  /** In the default layout for a fresh user. */
  readonly defaultEnabled: boolean;
};

const SURFACES = ['SME Mobile HTML'];
const BREAKPOINTS = ['narrow'] as const;

function widget(
  partial: Omit<
    MobileWidgetDefinition,
    'version' | 'supportedSurfaces' | 'supportedBreakpoints' | 'minSize' | 'maxSize'
  >,
): MobileWidgetDefinition {
  return {
    version: 1,
    supportedSurfaces: SURFACES,
    supportedBreakpoints: [...BREAKPOINTS],
    // Mobile is single-column: every widget is full width, one row tall.
    minSize: { w: 4, h: 1 },
    maxSize: { w: 4, h: 3 },
    ...partial,
  };
}

export const MOBILE_WIDGETS: readonly MobileWidgetDefinition[] = [
  widget({
    id: 'total-balance',
    module: 'Accounts & IBANs',
    title: 'Total balance',
    description: 'Your combined available balance, and a link to all accounts',
    // TT-R111-01: the total-balance module must exist on home.
    removable: false,
    pinnedIndex: 0,
    capability: 'accountsView',
    defaultEnabled: true,
    requiredPermissions: ['sme.accounts.view'],
    requiredFeatureFlags: ['accounts'],
    allowedConfigKeys: ['currency'],
  }),
  widget({
    id: 'quick-actions',
    module: 'Dashboard & Home',
    title: 'Quick actions',
    description: 'Named shortcuts to the tasks you do most',
    // TT-R100-02 + TT-R102-03: quick actions sit directly under the balance
    // module, in the upper region — never buried mid-page.
    removable: false,
    pinnedIndex: 1,
    defaultEnabled: true,
    requiredPermissions: [],
    requiredFeatureFlags: [],
    allowedConfigKeys: ['actions'],
  }),
  widget({
    id: 'needs-attention',
    module: 'Dashboard & Home',
    title: 'Needs attention',
    description: 'Approvals, failed payments and security items in one place',
    // TT-R101-02: near the top. TT-R105-02: this is the ONLY home location a
    // pending approval appears, so it cannot be removed and duplicated.
    removable: false,
    pinnedIndex: 2,
    defaultEnabled: true,
    requiredPermissions: [],
    requiredFeatureFlags: [],
    allowedConfigKeys: [],
  }),
  widget({
    id: 'money-flow',
    module: 'Reports & Exports',
    title: 'Money in and out',
    description: 'Totals for the period with a comparison',
    removable: true,
    capability: 'reportsView',
    defaultEnabled: true,
    requiredPermissions: ['sme.reports.view'],
    requiredFeatureFlags: ['reports'],
    allowedConfigKeys: ['periodDays'],
  }),
  widget({
    id: 'recent-activity',
    module: 'Transactions & Ledger',
    title: 'Recent activity',
    description: 'Your latest movements',
    removable: true,
    capability: 'transactionsView',
    defaultEnabled: true,
    requiredPermissions: ['sme.transactions.view'],
    requiredFeatureFlags: ['transactions'],
    allowedConfigKeys: ['limit'],
  }),
  widget({
    id: 'cards-summary',
    module: 'Cards',
    title: 'Cards',
    description: 'Your cards and their status',
    // TT-R112-01: a cards entry point on home, outside the quick-actions row.
    removable: true,
    capability: 'cardsView',
    defaultEnabled: true,
    requiredPermissions: ['sme.cards.view'],
    requiredFeatureFlags: ['cards'],
    allowedConfigKeys: [],
  }),
  widget({
    id: 'finance-shortcuts',
    module: 'Dashboard & Home',
    title: 'Finance',
    description: 'Treasury, invoices and the tax calendar',
    removable: true,
    capability: 'reportsView',
    defaultEnabled: true,
    requiredPermissions: ['sme.reports.view'],
    requiredFeatureFlags: ['reports'],
    allowedConfigKeys: [],
  }),
  widget({
    id: 'accounts-list',
    module: 'Accounts & IBANs',
    title: 'Accounts',
    description: 'Each account with its balance',
    // TT-R110-01: a bottom accounts section on home.
    removable: true,
    pinnedIndex: 99,
    capability: 'accountsView',
    defaultEnabled: true,
    requiredPermissions: ['sme.accounts.view'],
    requiredFeatureFlags: ['accounts'],
    allowedConfigKeys: [],
  }),
];

export function widgetById(id: string): MobileWidgetDefinition | undefined {
  return MOBILE_WIDGETS.find((w) => w.id === id);
}

export function isKnownWidget(id: string): id is MobileWidgetId {
  return MOBILE_WIDGETS.some((w) => w.id === id);
}

/** The governed default order for a user with no saved layout. */
export function defaultWidgetOrder(): readonly MobileWidgetId[] {
  return [...MOBILE_WIDGETS]
    .filter((w) => w.defaultEnabled)
    .sort((a, b) => (a.pinnedIndex ?? 50) - (b.pinnedIndex ?? 50))
    .map((w) => w.id);
}

/**
 * Apply the hard ordering constraints to any candidate order.
 *
 * Used by both manual reordering and the AI policy engine, so a pinned widget
 * cannot be displaced by either route.
 */
export function enforcePinning(order: readonly MobileWidgetId[]): readonly MobileWidgetId[] {
  const pinned = MOBILE_WIDGETS.filter((w) => w.pinnedIndex !== undefined).sort(
    (a, b) => (a.pinnedIndex ?? 0) - (b.pinnedIndex ?? 0),
  );
  const free = order.filter((id) => widgetById(id)?.pinnedIndex === undefined);

  const result: MobileWidgetId[] = [];
  const tail: MobileWidgetId[] = [];
  for (const w of pinned) {
    if (!order.includes(w.id)) continue;
    if ((w.pinnedIndex ?? 0) >= 90) tail.push(w.id);
    else result.push(w.id);
  }
  return [...result, ...free, ...tail];
}
